import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TailSpin } from 'react-loading-icons';
import { BACKEND_BASE_URL } from './Firebase.js';
import AppHeader from './AppHeader.jsx';
import LegalFooter from './LegalFooter.jsx';
import { getModernResultPath } from './resultPaths.js';

const getFilteredLegacyImages = (urls, qualities) => {
  const normalizedUrls = Array.isArray(urls)
    ? urls.filter((url) => typeof url === 'string' && url.trim())
    : [];
  const normalizedQualities = Array.isArray(qualities)
    ? qualities.map((value) => String(value || '').trim().toLowerCase())
    : [];

  if (normalizedQualities.length === 0) {
    return normalizedUrls;
  }

  const filtered = normalizedUrls.filter((_, index) => normalizedQualities[index] === 'good');
  return filtered.length > 0 ? filtered : normalizedUrls;
};

const LegacyResultsPage = () => {
  const navigate = useNavigate();
  const { docId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [legacyData, setLegacyData] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadPublicResult = async () => {
      const trimmedDocId = String(docId || '').trim();
      if (!trimmedDocId) {
        setError('Missing result id.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response = await fetch(`${BACKEND_BASE_URL}/public_result/${trimmedDocId}`);
        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(String(payload?.error || 'Could not load this result.'));
        }

        if (cancelled) {
          return;
        }

        if (payload?.kind === 'modern') {
          navigate(String(payload?.redirect_path || getModernResultPath(trimmedDocId)), { replace: true });
          return;
        }

        if (payload?.kind !== 'legacy') {
          throw new Error('This result format is not supported.');
        }

        setLegacyData(payload);
        setLoading(false);
      } catch (loadError) {
        if (cancelled) {
          return;
        }
        console.error('Failed to load legacy result:', loadError);
        setError(String(loadError?.message || loadError || 'Could not load this result.'));
        setLoading(false);
      }
    };

    loadPublicResult();

    return () => {
      cancelled = true;
    };
  }, [docId, navigate]);

  const petType = String(legacyData?.pet_type || '').trim().toLowerCase();
  const petBaby = petType === 'cat' ? 'kitten' : 'puppy';
  const petName = String(legacyData?.pet_name || '').trim();
  const primaryImages = useMemo(() => (
    getFilteredLegacyImages(legacyData?.output_images, legacyData?.image_quality)
  ), [legacyData?.image_quality, legacyData?.output_images]);
  const bonusImages = useMemo(() => (
    getFilteredLegacyImages(legacyData?.output_special_images, legacyData?.special_image_quality)
  ), [legacyData?.output_special_images, legacyData?.special_image_quality]);

  const title = petName
    ? `${petName}'s ${petBaby} photos`
    : `Your ${petBaby} photos`;

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <AppHeader />
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <TailSpin stroke="#8B5CF6" speed={0.75} width="48" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <AppHeader />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="rounded-3xl border border-red-200 bg-white p-6 text-center shadow-sm">
            <h1 className="text-2xl font-semibold text-slate-900">Could not load this result</h1>
            <p className="mt-3 text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <AppHeader />

      <main className="max-w-5xl mx-auto px-4 py-8">
        <section className="rounded-[2rem] border border-pink-100 bg-gradient-to-br from-white via-pink-50/60 to-purple-50/70 px-6 py-8 shadow-sm">
          <h1 className="text-3xl sm:text-4xl font-semibold leading-tight text-slate-900">
            {title}
          </h1>
        </section>

        <section className="mt-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900">Main photos</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {primaryImages.map((url, index) => (
              <div key={`${url}-${index}`} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                <img
                  src={url}
                  alt={`${title} ${index + 1}`}
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
            ))}
          </div>
        </section>

        {bonusImages.length > 0 ? (
          <section className="mt-10">
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900">Bonus photos</h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {bonusImages.map((url, index) => (
                <div key={`${url}-${index}`} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                  <img
                    src={url}
                    alt={`${title} bonus ${index + 1}`}
                    className="w-full aspect-[4/5] object-cover"
                  />
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <LegalFooter className="mt-10" />
      </main>
    </div>
  );
};

export default LegacyResultsPage;
